/**
 * "3 days ago" for a review, the way a review platform renders it.
 *
 * Reviews are stored with the date they were actually left, never with the
 * phrase that happened to be on screen when they were copied across. A
 * hardcoded "3 days ago" is true for three days and wrong for the rest of the
 * page's life, and a review that still claims to be from last week two years
 * later is exactly the detail that makes a visitor doubt everything else on
 * the page.
 *
 * The buckets are the ones Google itself uses — days up to a week, then
 * weeks, then months, then years. Nobody writes "37 days ago".
 */

/** Days in an average month, so "2 months" lands where a reader expects it. */
const DAYS_PER_MONTH = 30.44;

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"} ago`;

/**
 * @param date  The day the review was left, as `YYYY-MM-DD`.
 * @param now   Injected so the formatter is testable without faking the clock.
 * @returns     The label, or null when `date` is missing or unparseable — the
 *              caller renders no timestamp at all rather than "Invalid Date".
 */
export const formatReviewAge = (date: string | undefined, now: Date = new Date()): string | null => {
  if (!date) return null;

  // Parsed at midday UTC on purpose: a bare `YYYY-MM-DD` is parsed as UTC
  // midnight, which is the previous day for every visitor west of Greenwich
  // and would shift every review by one day for them.
  const then = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(then.getTime())) return null;

  const days = Math.floor((now.getTime() - then.getTime()) / 86_400_000);

  // A date in the future is a typo in the data, not something to render as
  // "-3 days ago".
  if (days < 0) return null;
  if (days < 1) return "today";
  if (days < 7) return plural(days, "day");
  if (days < 30) return plural(Math.floor(days / 7), "week");
  if (days < 365) return plural(Math.max(1, Math.round(days / DAYS_PER_MONTH)), "month");
  return plural(Math.floor(days / 365), "year");
};
